import React, { useMemo } from 'react';
import { IonIcon } from '@ionic/react';
import { bodyOutline } from 'ionicons/icons';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from 'recharts';
import './MostTrainedMusclesChart.css';

export interface MuscleData {
  muscle: string;
  count: number;
}

interface Props {
  data: MuscleData[];
}

const MostTrainedMusclesChart: React.FC<Props> = ({ data }) => {
  const max = useMemo(
    () => Math.max(1, ...data.map(item => Math.floor(item.count || 0))),
    [data]
  );

  const ticks = useMemo(() => {
    if (max <= 3) return Array.from({ length: max + 1 }, (_, index) => index);

    const roughStep = max / 3;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalized = roughStep / magnitude;
    const step =
      normalized <= 1
        ? magnitude
        : normalized <= 2
        ? 2 * magnitude
        : normalized <= 5
        ? 5 * magnitude
        : 10 * magnitude;
    const result: number[] = [];
    for (let value = 0; value <= max; value += step) result.push(value);
    if (result[result.length - 1] !== max) result.push(max);
    return result;
  }, [max]);

  const coveredMuscles = data.filter(item => item.count > 0).length;
  const totalMuscles = data.length;
  const coverage = Math.round(
    (coveredMuscles / Math.max(totalMuscles, 1)) * 100
  );

  return (
    <div className="muscle-spread-chart">
      <div className="muscle-spread-summary">
        <div className="muscle-spread-summary__icon">
          <IonIcon icon={bodyOutline} />
        </div>
        <div>
          <span>Muscle coverage</span>
          <strong>
            {coveredMuscles} of {totalMuscles} groups
          </strong>
        </div>
        <div className="muscle-spread-summary__coverage">{coverage}%</div>
      </div>

      <div className="muscle-spread-radar">
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart cx="50%" cy="50%" outerRadius="73%" data={data}>
            <defs>
              <linearGradient id="muscleSpreadFill" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#a887e4" stopOpacity={0.68} />
                <stop offset="52%" stopColor="#b9a2e8" stopOpacity={0.42} />
                <stop offset="100%" stopColor="#7eb4a4" stopOpacity={0.46} />
              </linearGradient>
            </defs>
            <PolarGrid stroke="rgba(130, 105, 181, 0.22)" />
            <PolarAngleAxis
              dataKey="muscle"
              tick={{ fill: '#71677b', fontSize: 10, fontWeight: 600 }}
            />
            <PolarRadiusAxis
              angle={30}
              domain={[0, max]}
              ticks={ticks as any}
              allowDecimals={false}
              tick={{ fill: '#998fa3', fontSize: 8 }}
              axisLine={false}
            />
            <Radar
              dataKey="count"
              stroke="#9c6dde"
              strokeWidth={2}
              fill="url(#muscleSpreadFill)"
              fillOpacity={1}
              dot={{ r: 3, fill: '#9c6dde', stroke: '#ffffff', strokeWidth: 1.5 }}
            />
          </RadarChart>
        </ResponsiveContainer>
        {coveredMuscles === 0 && (
          <div className="muscle-spread-radar__hint">
            Complete a workout to fill your muscle map.
          </div>
        )}
      </div>
    </div>
  );
};

export default MostTrainedMusclesChart;
